export type ActivityType = "guess-number" | "word-scramble" | "word-search" | "puzzle" | "response";
export type RoomPhase = "lobby" | "ready" | "countdown" | "live" | "results" | "aborted" | "ended";
export type RoomMode = "session" | "submission";
export type RegistrationFieldMode = "required" | "optional" | "hidden";

export interface RegistrationSettings {
  email: RegistrationFieldMode;
  organization: RegistrationFieldMode;
}

export const DEFAULT_REGISTRATION_SETTINGS: RegistrationSettings = {
  email: "required",
  organization: "required",
};

export interface WordPlacement {
  word: string;
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

export interface ScrambleItem {
  answer?: string;
  clue?: string;
  scrambled: string;
}

export interface GuessAttempt {
  value: number;
  hint: "higher" | "lower" | "correct";
}

export interface ActivityConfig {
  /** Legacy single-item fields retained so existing saved activities keep working. */
  answer?: string;
  clue?: string;
  scrambled?: string;
  scrambleItems?: ScrambleItem[];
  shuffleVersion?: number;
  timeLimit?: number;
  words?: string[];
  grid?: string[];
  placements?: WordPlacement[];
  imageKey?: string;
  imageUrl?: string;
  responseQuestions?: string[];
  /** Controls whether names and icons appear beside answers on the presenter wall. */
  responseShowNames?: boolean;
  /** Used when this activity is launched directly with Go Live. */
  registration?: RegistrationSettings;
}

export interface Activity {
  id: string;
  name: string;
  type: ActivityType;
  config: ActivityConfig;
  imageKey?: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface SessionTemplate {
  id: string;
  name: string;
  registration: RegistrationSettings;
  activities: Activity[];
  createdAt: number;
  updatedAt: number;
}

export interface RegistrationReportRow {
  id: string;
  reportDate: string;
  name: string;
  email: string;
  organization: string;
  firstJoinedAt: number;
  lastJoinedAt: number;
}

export interface RegistrationReportDay {
  date: string;
  count: number;
}

export interface ReportSessionSummary {
  openedAt: number;
  title: string;
  reportDate: string;
  attendeeCount: number;
  responseCount: number;
  submissionCount: number;
}

export interface SubmissionReportRow {
  id: string;
  reportDate: string;
  sessionTitle: string;
  participantName: string;
  url: string;
  updatedAt: number;
}

export interface ResponseReportRow {
  id: string;
  reportDate: string;
  sessionTitle: string;
  activityName: string;
  questionIndex: number;
  question: string;
  participantName: string;
  email: string;
  organization: string;
  answer: string;
  submittedAt: number;
}

export interface LineupItem {
  id: string;
  name: string;
  type: ActivityType;
}

export interface Participant {
  id: string;
  token: string;
  nickname: string;
  icon: string;
  joinedAt: number;
  registrationIdentityKey?: string;
}

export interface PlayerScore {
  participantId: string;
  completed: boolean;
  progress: number;
  attempts: number;
  completedAt?: number;
  foundWords?: string[];
  solvedItems?: number[];
  guessHint?: "higher" | "lower" | "correct";
  guessHistory?: GuessAttempt[];
  responses?: Record<string, string>;
  responseSubmittedAt?: Record<string, number>;
}

export interface ResponseWallEntry {
  participantId: string;
  nickname: string;
  icon: string;
  questionIndex: number;
  value: string;
  submittedAt: number;
}

export interface SubmissionEntry {
  participantId: string;
  nickname: string;
  icon: string;
  url: string;
  updatedAt: number;
}

export interface LeaderboardEntry {
  rank: number;
  participantId: string;
  nickname: string;
  icon: string;
  completed: boolean;
  progress: number;
  attempts: number;
  elapsedMs?: number;
}

export interface RoomPublicState {
  active: boolean;
  mode: RoomMode;
  title: string;
  joinCode: string;
  secureToken?: string;
  participantCount: number;
  participants: Array<Pick<Participant, "id" | "nickname" | "icon">>;
  phase: RoomPhase;
  activity?: Activity;
  countdownEndsAt?: number;
  liveStartedAt?: number;
  liveEndsAt?: number;
  leaderboard?: LeaderboardEntry[];
  eligibleCount?: number;
  completedCount?: number;
  responseWall?: ResponseWallEntry[];
  submissions?: SubmissionEntry[];
  inactivityEndsAt?: number;
  registration: RegistrationSettings;
  currentActivityNumber: number;
  totalActivities: number;
  hasNextActivity: boolean;
  lineup?: LineupItem[];
  player?: {
    participant: Pick<Participant, "id" | "nickname" | "icon">;
    eligible: boolean;
    score?: PlayerScore;
    guessHint?: "higher" | "lower" | "correct";
    submission?: SubmissionEntry;
  };
}

export interface StoredRoom {
  version: 2;
  active: boolean;
  mode: RoomMode;
  title: string;
  sessionTemplateId?: string;
  registration: RegistrationSettings;
  lineup: Activity[];
  currentActivityIndex: number;
  secureToken: string;
  joinCode: string;
  openedAt: number;
  participants: Record<string, Participant>;
  phase: RoomPhase;
  activity?: Activity;
  eligibleIds: string[];
  scores: Record<string, PlayerScore>;
  secretNumbers: Record<string, number>;
  liveStartedAt?: number;
  countdownEndsAt?: number;
  liveEndsAt?: number;
  leaderboard?: LeaderboardEntry[];
  submissions: Record<string, SubmissionEntry>;
  lastActivityAt?: number;
  inactivityEndsAt?: number;
}

export type ClientMessage =
  | { type: "guess"; value: number }
  | { type: "scramble_answer"; index: number; value: string }
  | { type: "word_found"; value: string }
  | { type: "puzzle_progress"; value: number; completed: boolean }
  | { type: "response_submit"; questionIndex: number; value: string }
  | { type: "submission_update"; url: string }
  | { type: "host_activity" }
  | { type: "sync" };

export type ServerMessage =
  | { type: "state"; state: RoomPublicState }
  | { type: "error"; message: string };
